import { makeStyles } from '@mui/styles';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Download from 'apollo-react-icons/Download';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import NavigateLinkCell from './NavigateLinkCell';
import { downloadAllAttachments } from '../../actions/AttachmentsDownloadAction';
import { translatePageTitle } from '../../actions/SavedSearchesActions';
import Layout from '../../components/Layout';
import NavigateLink from '../../components/NavigateLink';
import PageContainer from '../../components/PageContainer';
import SavedSearchesTable from '../../components/SavedSearchesTable';
import withTranslation from '../../components/withTranslation';
import { ENTITIES_WITH_VIEW_COLUMN } from '../../constants/Common';
import { DOCUMENT_VIEWER, ENTITY_DISPLAY } from '../../constants/Urls';
import { getIsSmartDocuments } from '../../selectors/MasterSetupPageSelectors';

const useStyles = makeStyles({
  loaderContainer: {
    paddingLeft: '5px',
  },
});

const getViewLinkQueryObject = (row, isPortalContent) =>
  isPortalContent
    ? {
        entityType: 'V_SS_SHARED_DOCUMENT_PRTL',
        docId: row.PK_ID,
        formEntityName: 'V_SS_SHARED_DOCUMENT_PRTL',
        formKey: 'SS_DOC_SHARE_PRTL_SHOW_REFERENCE',
        itmId: row.PARENT_GT_ID,
        isPortalContent,
      }
    : {
        entityType: row.EntityType,
        docId: row.ItmId,
        formEntityName: 'SDC_DOCUMENT',
        formKey: 'SDC_DOCUMENT_SHOW_REFERENCE',
        itmId: row.ItmId,
      };

const DownloadAllAttachmentsCell = ({ row, translateString, currentEntityViewName }) => {
  const [isLoading, setLoading] = useState(false);
  const classes = useStyles();
  const dispatch = useDispatch();
  const handleDownloadClick = async () => {
    setLoading(true);
    await dispatch(
      downloadAllAttachments({
        entityName: currentEntityViewName,
        itmId: row[row.PrimaryKeyColumn],
      })
    );
    setLoading(false);
  };

  return isLoading ? (
    <CircularProgress className={classes.loaderContainer} variant="indeterminate" size="30px" />
  ) : (
    <Tooltip placement="top" title={translateString('Download All Attachments')}>
      <IconButton
        onClick={handleDownloadClick}
        aria-label={translateString('Download All Attachments')}
      >
        <Download />
      </IconButton>
    </Tooltip>
  );
};

const FindPage = ({
  location,
  pageTitle,
  translatePageTitle,
  translateString,
  lastSelectedSearchNames = {},
}) => {
  const isSmartDocument = useSelector(getIsSmartDocuments);
  const currentEntityViewName = useSelector(
    (state) => state.savedSearchResults.currentEntityViewName
  );

  useEffect(() => {
    document.title = pageTitle
      ? `${pageTitle}: ${translateString('Saved Searches')}`
      : translateString('Saved Searches');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  useEffect(() => {
    const { entityName } = qs.parse(location.search);

    translatePageTitle(entityName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem('backButtonURL', location.pathname + location.search);
    };
  }, [location]);

  const { entityName, searchName } = qs.parse(location.search);
  const initialSearchName = lastSelectedSearchNames[entityName] ?? searchName;
  const isPortalContent = entityName === 'V_SS_SHARED_DOCUMENT_PRTL';

  function viewDocumentLink(row) {
    const link = `${DOCUMENT_VIEWER}?${qs.stringify(getViewLinkQueryObject(row, isPortalContent))}`;

    return (
      <NavigateLink to={link} target="_blank">
        {translateString('View')}
      </NavigateLink>
    );
  }

  const extraColumns = [
    {
      index: 0,
      header: '',
      fixedWidth: true,
      customCell: ({ row }) => viewDocumentLink(row),
    },
  ];

  const smartDocumentsExtraColumns = [
    {
      index: 99,
      header: translateString('Attachments'),
      customCell: ({ row }) =>
        DownloadAllAttachmentsCell({ row, translateString, currentEntityViewName }),
    },
  ];

  return (
    <Layout>
      <PageContainer>
        {_.isNil(entityName) ? (
          translateString('Please provide an entity name')
        ) : (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <SavedSearchesTable
                entityName={entityName}
                initialSearchName={initialSearchName}
                title={translateString('Records')}
                rowsPerPageOptions={[10, 25, 50]}
                enableSorting
                customCell={NavigateLinkCell.forUrl(ENTITY_DISPLAY)}
                extraColumns={
                  ENTITIES_WITH_VIEW_COLUMN.includes(entityName)
                    ? extraColumns
                    : isSmartDocument
                    ? smartDocumentsExtraColumns
                    : []
                }
                enableSaving={true}
                showFilters
              />
            </Grid>
          </Grid>
        )}
      </PageContainer>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  pageTitle: state.savedSearches.pageTitle,
  lastSelectedSearchNames: state.savedSearchFilters.lastSelectedSearchNames,
});

const mapDispatchToProps = {
  translatePageTitle,
};

const FindPageConnected = connect(mapStateToProps, mapDispatchToProps)(withTranslation(FindPage));

export default FindPageConnected;
